// =============================================================================
// TenantCostMatrix — tenant × vendor consumption for the Runway board's
// "Tenants" tab. Rows = tenants sorted by spend; columns = top vendors + Other;
// toggle $ cost / # usage; margin = period revenue − period cost.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Spinner } from '@/components/shared/UIComponents';
import { fetchTenantVendorMatrix, fetchTenantRevenueMap, type TenantVendorRow } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';

const VENDOR_LABEL: Record<string, string> = {
  anthropic: 'Claude', openai: 'OpenAI', google: 'Gemini', google_places: 'Maps',
  fal: 'fal.ai', creatomate: 'Creatomate', duffel: 'Duffel', stripe: 'Stripe',
};
const label = (v: string) => VENDOR_LABEL[v] ?? v;
const usd = (c: number) => '$' + (Math.round(c) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
const MAX_COLS = 5;

export function TenantCostMatrix({ startIso, endIso, days }: { startIso: string; endIso: string; days: number }) {
  const [rows, setRows] = useState<TenantVendorRow[]>([]);
  const [revMap, setRevMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'cost' | 'count'>('cost');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([fetchTenantVendorMatrix(startIso, endIso), fetchTenantRevenueMap()])
      .then(([r, rev]) => { if (alive) { setRows(r); setRevMap(rev); } })
      .catch(e => toast.error(errorMessage(e) || 'Failed to load tenant costs'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [startIso, endIso]);

  // Column vendors = top MAX_COLS by total cost; the rest fold into "Other".
  const cols = useMemo(() => {
    const byVendor = new Map<string, number>();
    for (const r of rows) byVendor.set(r.vendor, (byVendor.get(r.vendor) ?? 0) + Number(r.cost_cents || 0));
    const ranked = [...byVendor.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
    return { top: ranked.slice(0, MAX_COLS), hasOther: ranked.length > MAX_COLS };
  }, [rows]);

  const tenants = useMemo(() => {
    const m = new Map<string, { id: string; name: string; cells: Record<string, number>; other: number; total: number; count: number }>();
    for (const r of rows) {
      const cur = m.get(r.tenant_id) ?? { id: r.tenant_id, name: r.tenant_name, cells: {}, other: 0, total: 0, count: 0 };
      const val = mode === 'cost' ? Number(r.cost_cents || 0) : Number(r.event_count || 0);
      if (cols.top.includes(r.vendor)) cur.cells[r.vendor] = (cur.cells[r.vendor] ?? 0) + val;
      else cur.other += val;
      cur.total += val;
      cur.count += Number(r.event_count || 0);
      m.set(r.tenant_id, cur);
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [rows, cols, mode]);

  const fmt = (n: number) => (mode === 'cost' ? usd(n) : Math.round(n).toLocaleString());
  const revForPeriod = (id: string) => (revMap[id] ?? 0) * (days / 30);

  const totals = useMemo(() => {
    const t = { cells: {} as Record<string, number>, other: 0, total: 0, rev: 0 };
    for (const ten of tenants) {
      for (const v of cols.top) t.cells[v] = (t.cells[v] ?? 0) + (ten.cells[v] ?? 0);
      t.other += ten.other; t.total += ten.total; t.rev += revForPeriod(ten.id);
    }
    return t;
  }, [tenants, cols, revMap, days]);

  if (loading) return <div className="py-16 flex justify-center"><Spinner /></div>;

  const marginCell = (rev: number, cost: number) => {
    const m = rev - cost;
    return <span className={m >= 0 ? 'text-emerald-600' : 'text-red-600'}>{(m >= 0 ? '+' : '−') + usd(Math.abs(m))}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-surface-500">Who's driving spend — sorted by total. Margin = revenue − cost over {days}d.</p>
        <div className="inline-flex rounded-lg border border-surface-200 overflow-hidden text-sm">
          <button onClick={() => setMode('cost')} className={`px-3 py-1.5 ${mode === 'cost' ? 'bg-surface-100 text-surface-900 font-medium' : 'text-surface-500'}`}>$ cost</button>
          <button onClick={() => setMode('count')} className={`px-3 py-1.5 ${mode === 'count' ? 'bg-surface-100 text-surface-900 font-medium' : 'text-surface-500'}`}># usage</button>
        </div>
      </div>

      {tenants.length === 0 ? (
        <p className="text-sm text-surface-500 py-10 text-center">No attributed tenant spend in this period.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-surface-400 text-xs">
                <th className="text-left font-normal py-2 pr-3">Tenant</th>
                {cols.top.map(v => <th key={v} className="text-right font-normal py-2 px-2">{label(v)}</th>)}
                {cols.hasOther && <th className="text-right font-normal py-2 px-2">Other</th>}
                <th className="text-right font-normal py-2 px-2">Total</th>
                {mode === 'cost' && <th className="text-right font-normal py-2 px-2">Revenue</th>}
                {mode === 'cost' && <th className="text-right font-normal py-2 pl-2">Margin</th>}
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => {
                const rev = revForPeriod(t.id);
                return (
                  <tr key={t.id} className="border-t border-surface-100">
                    <td className="py-2 pr-3 text-surface-900 font-medium truncate max-w-[180px]">{t.name}</td>
                    {cols.top.map(v => <td key={v} className="text-right py-2 px-2 text-surface-600 tabular-nums">{t.cells[v] ? fmt(t.cells[v]) : '·'}</td>)}
                    {cols.hasOther && <td className="text-right py-2 px-2 text-surface-600 tabular-nums">{t.other ? fmt(t.other) : '·'}</td>}
                    <td className="text-right py-2 px-2 text-surface-900 font-medium tabular-nums">{fmt(t.total)}</td>
                    {mode === 'cost' && <td className="text-right py-2 px-2 text-surface-500 tabular-nums">{rev ? usd(rev) : '—'}</td>}
                    {mode === 'cost' && <td className="text-right py-2 pl-2 tabular-nums">{rev || t.total ? marginCell(rev, t.total) : '—'}</td>}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-surface-300 text-surface-500">
                <td className="py-2 pr-3 text-left">All tenants</td>
                {cols.top.map(v => <td key={v} className="text-right py-2 px-2 tabular-nums">{fmt(totals.cells[v] ?? 0)}</td>)}
                {cols.hasOther && <td className="text-right py-2 px-2 tabular-nums">{fmt(totals.other)}</td>}
                <td className="text-right py-2 px-2 text-surface-900 font-medium tabular-nums">{fmt(totals.total)}</td>
                {mode === 'cost' && <td className="text-right py-2 px-2 tabular-nums">{usd(totals.rev)}</td>}
                {mode === 'cost' && <td className="text-right py-2 pl-2 tabular-nums">{marginCell(totals.rev, totals.total)}</td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="text-xs text-surface-400 mt-3">
        Client-side Google Maps/Places calls carry no tenant id and aren't shown here. Flat subs (Supabase, Netlify, Resend, Claude Code) are platform overhead, not per-tenant.
      </p>
    </div>
  );
}
